import { Select } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { sortBy } from "lodash-es";

import {
  getGetBookingQueryKey,
  getGetBookingsQueryKey,
  useUpdateBookingProgram,
} from "@/shared/api/generated/booking";
import { useGetEmployees } from "@/shared/api/generated/employee";
import { GetBookingsItem } from "@/shared/api/generated/schemas";
import {
  GetFieldsBySubjectKind,
  getSubject,
} from "@/shared/providers/casl-provider/authorization/subjects";
import { useAppAbility } from "@/shared/providers/casl-provider/use-app-ability";

export default function UpdateBookingGuide({
  booking,
}: {
  booking: GetBookingsItem;
}) {
  const { data: employees } = useGetEmployees({
    locations: booking.location?.id ? [booking.location.id] : undefined,
  });

  const employeeOptions =
    employees?.data?.map((employee) => ({
      label: `${employee.firstName} ${employee.lastName}`,
      value: employee.id,
    })) ?? [];

  const queryClient = useQueryClient();

  const { mutate: updateProgram } = useUpdateBookingProgram({
    mutation: {
      onSettled: (_data, _error, variables) => {
        void queryClient.invalidateQueries({
          queryKey: getGetBookingsQueryKey(),
        });
        void queryClient.removeQueries({
          queryKey: getGetBookingQueryKey(variables.id),
        });
      },
    },
  });

  const ability = useAppAbility();

  const canUpdateBooking = (field: GetFieldsBySubjectKind<"Booking">) =>
    ability.can("update", getSubject("Booking", booking), field);

  return (
    <Select
      size="xs"
      value={booking.guide?.id}
      data={sortBy(employeeOptions, (o) => o.label.toLowerCase())}
      clearable
      onChange={(value) => {
        updateProgram({
          id: booking.id,
          data: {
            guideId: value,
            products: booking.program ?? null,
            updatedAt: booking.updatedAt,
          },
        });
      }}
      readOnly={!canUpdateBooking("guide")}
      // default border color is not set by Input overrides done in MantineProvider (readOnly prop is always true)
      // perhaps because it is in a table, set it manually here
      styles={{
        wrapper: {
          "--input-bd": canUpdateBooking("guide")
            ? "var(--mantine-color-primary-4)"
            : undefined,
        },
      }}
    />
  );
}
